'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { OAuthModal } from './OAuthModal';
import { useOptionalLoginModals } from '../common/LoginModalsManager';
import { exchangeSsoCode, authErrorMessage, completeSsoRegistration } from '@/lib/auth/common/client';
import { classifySsoRegistrationFailure } from '@/lib/auth/common/sso-registration-failure';
import { getOAuthRedirectUri } from '@/lib/auth/idp-urls';
import { finalizeClientLogin } from '@/lib/auth/common/finalize-login';
import {
  buildAbsoluteRedirectUrl,
  resolvePostLoginUrl,
  storePreviousPath,
} from '@/utils/auth/common/auth-storage';
import { useLoginPromptStore } from '@/store/login-prompt-store';
import { deleteQueryParam } from '@/lib/utils/url-params';

/**
 * Codes already spent. An OAuth `?code=` is single-use: a second GET of
 * /api/auth/sso/callback with the same code gets a 400 from the IdP.
 *
 * Module-scoped rather than a ref so the guard holds across mounts, not just across
 * StrictMode's double effect invoke. A mount after the strip below sees no `?code=` at all;
 * this is the backstop for one that captured the code before the strip landed.
 */
const exchangedSsoCodes = new Set<string>();

/**
 * Cap the guard set so a long-lived tab can't accumulate codes indefinitely. Only the
 * most recent codes matter — the duplicate exchanges this guards against happen within
 * one callback, not hours later.
 */
const MAX_TRACKED_SSO_CODES = 20;

function rememberSsoCode(code: string): void {
  exchangedSsoCodes.add(code);
  // Grows one at a time, so it can only ever be one over the cap. Destructuring the Set
  // yields the oldest entry, already typed — no cast needed.
  const [oldest] = exchangedSsoCodes;
  if (exchangedSsoCodes.size > MAX_TRACKED_SSO_CODES) exchangedSsoCodes.delete(oldest);
}

/**
 * Take the callback params out of the address bar. Called once, BEFORE the exchange rather
 * than after it succeeds: the code is spent the moment it is sent, so leaving it in the URL
 * on the failure paths only invited a reload to re-spend it and fail a second time with a
 * different (IdP-level) message.
 *
 * This DOES reach this render tree: Next's router syncs history.replaceState into
 * useSearchParams(), so `code` reads null right afterwards. That is why the component below
 * captures the params on its first render instead of reading them live.
 */
function stripOAuthParams(): void {
  ['code', 'region', 'location'].forEach((param) => deleteQueryParam(param));
}

/**
 * Per-status wording for a failed exchange whose body carries no `message`, such as an
 * unhandled Next 500 or a proxy's HTML error page. An upstream failure never gets here:
 * handleCallback() always answers "SSO sign-in failed.". Anything unlisted falls back to
 * the status.
 */
const STATUS_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your login credentials and try again.',
  401: 'Authentication failed. Please try again.',
  403: 'Access denied. Please contact support.',
  404: 'Login service not found. Please try again later.',
  500: 'Server error. Please try again later.',
};

/** One callback lifecycle, as a single value instead of three booleans/strings that could drift out of sync (e.g. spinner + error both true). */
type RegistrationValues = { role: string; company: string };

type CallbackPhase =
  | { kind: 'idle' }
  | { kind: 'processing' }
  // `notice`/`values` are set only when a registration attempt failed and is worth retrying.
  | { kind: 'needs-registration'; notice?: string; values?: RegistrationValues }
  | { kind: 'error'; message: string };

/**
 * THE Contentstack SSO return leg, whole. Mounted on the landing page only.
 *
 * The IdP redirects to the app ROOT with `?code=` (`/academy?code=…`) — there is no /login
 * route any more. Mounting this on one page rather than in the layout is deliberate: a
 * layout-wide instance would read `?code=` off every route and hand an unrelated query
 * param to the IdP as an authorization code.
 *
 * This used to be split across a `useOAuthCallback` hook and this component, which owned
 * the 422 continuation. The hook had exactly one caller and the two halves each carried
 * their own copy of the post-login redirect tail, so a fix to one could miss the other.
 * With that tail now behind resolvePostLoginUrl(), there was nothing left for the boundary
 * to buy — both halves of the same handshake live here.
 *
 * Renders nothing at all when there is no callback in progress.
 */
export function SsoCallbackHandler() {
  const searchParams = useSearchParams();

  /**
   * The callback params as they were on the FIRST render. Read live, they went null the
   * moment stripOAuthParams() ran (see above), which nulled `oauthData`. The 422
   * registration modal then never rendered, so a new SSO user could not finish signing up.
   * The IdP return is a full page load, so every callback is a fresh mount and one capture
   * per mount is enough.
   */
  const [{ code, region, location }] = useState(() => ({
    code: searchParams?.get('code') ?? null,
    region: searchParams?.get('region') ?? null,
    location: searchParams?.get('location') ?? null,
  }));

  /**
   * Seeded on the FIRST render rather than switched on in the effect below. Starting at
   * `idle` meant the mount that is about to exchange a code still rendered one frame of
   * "not busy", which read as a flash of the page behind before "Processing login…"
   * replaced it.
   *
   * The `exchangedSsoCodes` check is what keeps it honest: on a remount carrying a code
   * that was already spent, the effect returns early and would never move this out of
   * `processing`, leaving a spinner that never resolves. In that case there is no exchange
   * to wait for, so this correctly starts at `idle`.
   */
  const [phase, setPhase] = useState<CallbackPhase>(() =>
    code !== null && !exchangedSsoCodes.has(code) ? { kind: 'processing' } : { kind: 'idle' }
  );
  // Selector, not a whole-store subscription: this is mounted on every landing-page load,
  // so subscribing to every field re-rendered it on unrelated prompt-counter writes.
  const resetLoginPrompt = useLoginPromptStore((s) => s.resetAll);

  /**
   * Derived, not state: it is a pure function of the captured params and the environment,
   * so storing it meant a setState in the effect body (a cascading render) to hold a value
   * nothing had computed differently.
   *
   * getOAuthRedirectUri() reads window.location when NEXT_PUBLIC_OAUTH_CLIENT_URL is unset
   * (empty during SSR) — harmless since `phase` is never `needs-registration` on first render.
   */
  const oauthData = useMemo(
    () => (code ? { region: region || '', redirect_uri: getOAuthRedirectUri() } : null),
    [code, region]
  );

  // The failure has to be readable where the SSO buttons are, and that is the modal.
  const openLoginModalWithNotice = useOptionalLoginModals()?.openLoginModalWithNotice;

  // Partner SSO (Impartner) is not handled here: the Academy API establishes the session at
  // its own SAML ACS callback and redirects back already authenticated. That return leg is
  // a session check — see components/Login/saml/PartnerSessionSync.tsx.

  // `?location=` can carry the page the user started from (the region codes NA/EU/APAC use
  // the same param, hence the exclusions).
  useEffect(() => {
    if (!code || !location || location === 'NA' || location === 'EU' || location === 'APAC') return;
    try {
      const decoded = decodeURIComponent(location);
      if (decoded.startsWith('/') || decoded.startsWith('http')) storePreviousPath(decoded);
    } catch {
      // A malformed percent-encoding is not worth failing the login over.
    }
  }, [location, code]);

  // Spend the `?code=` for a session.
  useEffect(() => {
    if (!code) return;
    // Single-use code: whoever gets here first owns the exchange. StrictMode's second
    // invoke, and any remount, bail out before the GET.
    if (exchangedSsoCodes.has(code)) return;
    rememberSsoCode(code);

    // No setPhase('processing') here: the useState seed above already starts there.
    stripOAuthParams();

    // CRITICAL: must exactly match what was sent on the authorize leg (handleSSOLogin in
    // LoginModal.tsx) — hence one shared function rather than two copies of the logic.
    // Non-null: oauthData is memoized as non-null for exactly `code !== null`, and the
    // guard above already returned when there is no code. The `??` fallback this used to
    // carry was a second, silently-drifting copy of how redirect_uri is derived.
    const { region: regionParam, redirect_uri: redirectUri } = oauthData!;

    (async () => {
      if (!redirectUri) {
        setPhase({ kind: 'error', message: 'Missing required OAuth parameters. Please try again.' });
        return;
      }

      try {
        // Same-origin — goes through this app's own route (see
        // app/api/auth/sso/callback/route.ts), which forwards to the Academy API's
        // GET /v1/auth/contentstack/callback and relays its Set-Cookie back.
        // redirect_uri is the app ROOT and is only echoed here: it must match the
        // authorize leg byte-for-byte, and it is what the IdP has registered.
        const { ok, status, body } = await exchangeSsoCode({
          region: regionParam,
          code,
          redirectUri,
        });

        if (!ok) {
          // 422 is not a failure: the IdP authenticated someone with no Academy profile
          // yet, so collect company/role and finish via handleOauthSubmit below.
          if (status === 422) {
            setPhase({ kind: 'needs-registration' });
            return;
          }
          const fallback = STATUS_MESSAGES[status] ?? `Login failed with status ${status}. Please try again.`;
          setPhase({ kind: 'error', message: authErrorMessage(body, fallback) });
          return;
        }

        // Uses the auth response for the user object rather than calling GET /v1/users —
        // that endpoint rotates the single-use Contentstack refresh token and makes two
        // Contentstack calls to return what this response already carries.
        if (!finalizeClientLogin(body)) {
          setPhase({ kind: 'error', message: 'Error processing login data. Please try again.' });
          return;
        }

        resetLoginPrompt();

        // Destination: the path stored when SSO login was clicked, else the home page.
        // resolvePostLoginUrl() owns reading, validating, allowlisting, absolutising and
        // clearing it. excludeProgressPages: resuming straight into an
        // /assessment would read progress this login just wrote.
        // replace(), not assign(), so Back doesn't return to a spent callback URL.
        window.location.replace(
          resolvePostLoginUrl({ excludeProgressPages: true }) ?? buildAbsoluteRedirectUrl('/')
        );
        // No setPhase() here — the line above navigates away, so nothing re-renders.
      } catch (error) {
        setPhase({
          kind: 'error',
          message:
            error instanceof Error && error.message
              ? error.message
              : 'An error occurred during login. Please try again.',
        });
      }
    })();
  }, [code, oauthData, resetLoginPrompt]);

  /**
   * Second half of a 422 callback: the user supplies company/role and we finish the
   * sign-in. OAuthModal closes itself on submit, so every failure branch must move the
   * phase on — otherwise the attempt ends silently, with the `?code=` already spent.
   *
   * Only a retryable failure reopens the modal (with what they typed, and why). Reopening
   * it on a 4xx looped: the resubmit could only repeat the same rejection.
   */
  const handleOauthSubmit = useCallback(
    (data: RegistrationValues) => {
      if (!oauthData) return;
      // 'idle' hides the modal while submitting; OAuthModal owns its own in-flight UI.
      setPhase({ kind: 'idle' });

      const retry = (notice: string) => setPhase({ kind: 'needs-registration', notice, values: data });

      completeSsoRegistration(data, { region: oauthData.region, redirectUri: oauthData.redirect_uri })
        .then(({ ok, status, body }) => {
          if (!ok) {
            const failure = classifySsoRegistrationFailure(status);
            if (failure.retryable) retry(failure.notice);
            else setPhase({ kind: 'error', message: failure.message });
            return;
          }

          // Registered and signed in server-side, but the client copy couldn't be written
          // (private mode/quota). Resubmitting wouldn't fix that, so fail like the callback does.
          if (!finalizeClientLogin(body)) {
            setPhase({ kind: 'error', message: 'Error processing login data. Please try again.' });
            return;
          }

          // No URL cleanup here: the effect above strips the callback params before it
          // spends the code, so by now they are already gone.
          //
          // Same resolver as the success branch — one ruleset for both halves of the
          // return leg. Only the default differs: someone who just registered has no
          // meaningful page to resume, so /catalog.
          window.location.replace(
            resolvePostLoginUrl({ excludeProgressPages: true }) ?? buildAbsoluteRedirectUrl('/catalog')
          );
        })
        // No response from this app's own route (offline, blip). Whether the token was
        // rotated is unknown, and retrying is the only way to find out.
        .catch(() => retry('Something went wrong. Please check your connection and try again.'));
    },
    [oauthData]
  );

  // A failed exchange has nowhere of its own to be shown — this component renders null in
  // that state — so hand the message to the modal.
  useEffect(() => {
    if (phase.kind === 'error') openLoginModalWithNotice?.(phase.message);
  }, [phase, openLoginModalWithNotice]);

  if (phase.kind === 'processing') {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
        <div className="rounded-lg bg-surface-1 p-8 text-center shadow-lg">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-border-light border-t-icon-strong" />
          <p className="cs-s-body text-text-default-medium">Processing login...</p>
        </div>
      </div>
    );
  }

  if (phase.kind === 'needs-registration' && oauthData) {
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
        <OAuthModal
          onClose={() => setPhase({ kind: 'idle' })}
          onSubmit={handleOauthSubmit}
          notice={phase.notice}
          initialValues={phase.values}
          isOpen
        />
      </div>
    );
  }

  return null;
}
